import React from "react";
import {Localizer} from "@/main/react/Types";
import DateFormatter from "@/main/common/DateFormatter";
import { getDeletedContentReasonLabel } from "@/main/react/contentDeletion/ContentDeletionLocalization";

export type ThoughtLifecycleFields = {
    isDeleted?: boolean | null;
    deletedAt?: string | null;
    deletedByModerator?: boolean | null;
    deletedReasonType?: string | null;
    canDelete?: boolean | null;
    canStartConversation?: boolean | null;
    htmlContent?: string | null;
};

export function isArgumentDeleted(argument?: ThoughtLifecycleFields | null): boolean {
    return Boolean(argument?.isDeleted);
}

export function canDeleteArgument(argument?: ThoughtLifecycleFields | null): boolean {
    return Boolean(argument?.canDelete) && !isArgumentDeleted(argument);
}

/**
 * Editable replies (design §3.6): the author may edit their own reply while it is not deleted.
 *
 * Deliberately the same predicate as {@link canDeleteArgument}, read off the same server-resolved
 * `canDelete` ("the viewer is the author and the reply is live") rather than a username comparison the
 * client would have to keep in step with the server. The server is the authority either way: a stale
 * `true` ends as its own refusal, never as a write.
 */
export function canEditThought(argument?: ThoughtLifecycleFields | null): boolean {
    return Boolean(argument?.canDelete) && !isArgumentDeleted(argument);
}

export function canStartConversationFromArgument(argument?: ThoughtLifecycleFields | null): boolean {
    if (!argument) {
        return true;
    }
    if (isArgumentDeleted(argument)) {
        return false;
    }
    return argument.canStartConversation !== false;
}

export function ArgumentDeletedTombstone(props: {
    localizer: Localizer;
    deletedAt?: string | null;
    deletedByModerator?: boolean | null;
    deletedReasonType?: string | null;
    className?: string;
}) {
    const {localizer, deletedAt, deletedByModerator, deletedReasonType, className = ""} = props;
    const dateFormatter = new DateFormatter(localizer);
    const deletedAtLabel = deletedAt ? dateFormatter.timeSince(deletedAt) : null;
    const deletedReasonLabel = getDeletedContentReasonLabel(localizer, deletedReasonType);
    const labelKey = deletedByModerator
        ? "typescript.thought.deleted.label_moderator"
        : "typescript.thought.deleted.label";
    const body = deletedByModerator
        ? deletedReasonLabel
            ? localizer("typescript.thought.deleted.body_moderator_with_reason", deletedReasonLabel)
            : localizer("typescript.thought.deleted.body_moderator")
        : localizer("typescript.thought.deleted.body");

    return (
        <div
            data-playwright-id="thought-deleted-tombstone"
            className={`rounded-2xl border border-dashed border-border bg-background2/60 px-4 py-3 text-sm ${className}`.trim()}
        >
            <div className="flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-neutral-text/90">
                <span>{localizer(labelKey)}</span>
                {deletedAtLabel ? <span className="normal-case tracking-normal font-normal">{localizer("typescript.thought.deleted.when", deletedAtLabel)}</span> : null}
            </div>
            <p className="mt-2 text-sm text-text-main/85">{body}</p>
        </div>
    );
}
